import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { listData, createData, deleteData, fmtMoney, fmtNum } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UsersThree, ArrowsClockwise, CurrencyCircleDollar, UserPlus, Plus, Trash } from "@phosphor-icons/react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

const tip = { contentStyle: { background: "#10101A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10 } };
const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export default function CustomersPage() {
  const { activeId } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const subKey = ["data", "subscription_metrics", activeId];
  const { data: subs = [] } = useQuery({ queryKey: subKey, queryFn: () => listData("subscription_metrics", activeId), enabled: !!activeId });
  const { data: kpis = [] } = useQuery({ queryKey: ["data", "kpi_metrics", activeId], queryFn: () => listData("kpi_metrics", activeId), enabled: !!activeId });
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(null);
  const inv = () => { qc.invalidateQueries({ queryKey: subKey }); qc.invalidateQueries({ queryKey: ["dashboard", activeId] }); };

  const saveM = useMutation({ mutationFn: (d) => createData("subscription_metrics", { ...d, client_id: activeId }), onSuccess: () => { inv(); toast.success("Added"); } });
  const delM = useMutation({ mutationFn: (id) => deleteData("subscription_metrics", id), onSuccess: () => { inv(); toast.success("Removed"); } });

  const latest = subs[subResolve(subs)] || {};
  const custData = kpis.map((k) => ({ date: k.date, new: k.new_customers, repeat: k.repeat_customers }));

  return (
    <PageContainer>
      <PageHeader eyebrow="Retention" title="Customers & Subscriptions"
        subtitle="Track new vs repeat customers and subscription growth — the engine of recurring revenue.">
        <Button data-testid="add-sub-btn" onClick={() => { setCur({ date: new Date().toISOString().slice(0, 10), active_subscribers: 0, new: 0, churned: 0, mrr: 0 }); setOpen(true); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plus size={16} className="mr-1.5" /> Add snapshot</Button>
      </PageHeader>

      {subs.length === 0 && kpis.length === 0 ? (
        <div className="card-matte p-10 text-center" data-testid="customers-empty-state">
          <UsersThree size={36} className="mx-auto text-[#5a5a5a] mb-3" />
          <div className="font-head font-semibold text-lg">No subscription data available</div>
          <p className="text-sm text-[#9a9a9a] mt-1 max-w-md mx-auto">Vozelto never invents customer numbers. Add subscription snapshots manually — or view live customer counts from your connected Shopify store.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button data-testid="customers-empty-add-btn" onClick={() => { setCur({ date: new Date().toISOString().slice(0, 10), active_subscribers: 0, new: 0, churned: 0, mrr: 0 }); setOpen(true); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plus size={16} className="mr-1.5" /> Add snapshot</Button>
            <Button data-testid="customers-empty-shopify-btn" variant="ghost" onClick={() => navigate("/shopify")} className="border border-white/15 text-white hover:bg-white/5">View Shopify customers</Button>
          </div>
        </div>
      ) : (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Active Subscribers" value={fmtNum(latest.active_subscribers)} icon={UsersThree} accent />
        <StatCard label="MRR" value={fmtMoney(latest.mrr)} icon={CurrencyCircleDollar} />
        <StatCard label="New (last snap)" value={fmtNum(latest.new)} icon={UserPlus} />
        <StatCard label="Churned (last snap)" value={fmtNum(latest.churned)} icon={ArrowsClockwise} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="card-matte p-5">
          <div className="label-caps mb-4">New vs Repeat Customers</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={custData} margin={{ left: -10 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip {...tip} labelFormatter={fmtDate} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="new" name="New" stackId="a" fill="#6B3DFF" radius={[0, 0, 0, 0]} />
              <Bar dataKey="repeat" name="Repeat" stackId="a" fill="#34D399" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-matte p-5">
          <div className="label-caps mb-4">Subscription Growth</div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={subs}>
              <defs><linearGradient id="sub" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34D399" stopOpacity={0.5} /><stop offset="100%" stopColor="#34D399" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip {...tip} labelFormatter={fmtDate} />
              <Area type="monotone" dataKey="active_subscribers" name="Active subs" stroke="#34D399" strokeWidth={2.5} fill="url(#sub)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-matte overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
              {["Date", "Active", "New", "Churned", "MRR", ""].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {subs.map((s) => (
                <TableRow key={s.id} className="border-white/8 hover:bg-white/[0.03]" data-testid="sub-row">
                  <TableCell>{s.date}</TableCell><TableCell>{s.active_subscribers}</TableCell>
                  <TableCell className="text-emerald-400">+{s.new}</TableCell><TableCell className="text-red-400">-{s.churned}</TableCell>
                  <TableCell>{fmtMoney(s.mrr)}</TableCell>
                  <TableCell className="text-right"><button onClick={() => delM.mutate(s.id)} className="text-[#9a9a9a] hover:text-red-400 p-1.5"><Trash size={15} /></button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-head">Add subscription snapshot</DialogTitle></DialogHeader>
          {cur && (
            <div className="space-y-3">
              <div><Label className="label-caps">Date</Label><Input type="date" value={cur.date} onChange={(e) => setCur({ ...cur, date: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Active subscribers</Label><Input type="number" value={cur.active_subscribers} onChange={(e) => setCur({ ...cur, active_subscribers: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" data-testid="sub-active" /></div>
                <div><Label className="label-caps">MRR</Label><Input type="number" value={cur.mrr} onChange={(e) => setCur({ ...cur, mrr: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">New</Label><Input type="number" value={cur.new} onChange={(e) => setCur({ ...cur, new: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">Churned</Label><Input type="number" value={cur.churned} onChange={(e) => setCur({ ...cur, churned: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-sub-btn" onClick={() => { saveM.mutate(cur); setOpen(false); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function subResolve(subs) { return subs.length ? subs.length - 1 : 0; }
